'use client';

import { useEffect, useRef, useState } from 'react';
import '@/styles/phone-input.css';
import '@/styles/contact.css';

export default function ContactSection() {
  const contactFormRef = useRef<HTMLFormElement>(null);
  const phoneBackFormRef = useRef<HTMLFormElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const messagePhoneInputRef = useRef<HTMLInputElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    // Email protection
    const emailPlaceholder = document.getElementById('email-placeholder');
    if (emailPlaceholder) {
      const user = 'hello';
      const domain = 'zappingai.com';
      const emailLink = document.createElement('a');
      emailLink.href = `mailto:${user}@${domain}`;
      emailLink.className = 'email-link';
      emailLink.textContent = `${user}@${domain}`;
      emailPlaceholder.parentNode?.replaceChild(emailLink, emailPlaceholder);
    }

    // Load Scripts
    const loadScripts = () => {
      if ((window as any).intlTelInput) {
        setIsScriptLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src =
        'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/intlTelInput.min.js';
      script.async = true;
      script.onload = () => {
        const utilsScript = document.createElement('script');
        utilsScript.src =
          'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js';
        utilsScript.onload = () => setIsScriptLoaded(true);
        document.body.appendChild(utilsScript);
      };
      document.body.appendChild(script);

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href =
        'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/css/intlTelInput.css';
      document.head.appendChild(link);
    };

    loadScripts();
  }, []);

  useEffect(() => {
    if (isScriptLoaded && (window as any).intlTelInput) {
      const initPhoneInput = (input: HTMLInputElement | null) => {
        if (input && !(input as any).classList.contains('iti__tel-input')) {
          (window as any).intlTelInput(input, {
            initialCountry: 'gb',
            utilsScript:
              'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js',
            allowDropdown: true,
            separateDialCode: true,
            dropdownContainer: document.body,
          });
          // Remove aria-activedescendant if set to a non-existent element
          const selectedFlag = input.closest('.iti')?.querySelector('.iti__selected-flag');
          if (selectedFlag) {
            selectedFlag.removeAttribute('aria-activedescendant');
          }
        }
      };

      const initTimer = setTimeout(() => {
        initPhoneInput(phoneInputRef.current);
        initPhoneInput(messagePhoneInputRef.current);
      }, 100);

      return () => clearTimeout(initTimer);
    }
  }, [isScriptLoaded]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      company: formData.get('company') as string,
      message: formData.get('message') as string,
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'Direct',
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        alert('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        alert(`Error: ${result.error || 'Failed to send message.'}`);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const handlePhoneBackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const phoneInput = phoneInputRef.current; // Use ref

    if (!phoneInput?.value) return;

    const data = {
      phone: phoneInput.value,
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'Direct',
    };

    try {
      const response = await fetch('/api/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`We'll call you back at ${phoneInput.value} soon!`);
        phoneInput.value = '';
      } else {
        alert(`Error: ${result.error || 'Failed to send request.'}`);
      }
    } catch (error) {
      console.error('Phone back form error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="contact-right relative w-full overflow-visible rounded-3xl border border-zinc-300 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white to-gray-100 shadow-none transition-shadow dark:border-zinc-600 dark:bg-[#2f2f2f]">
      <div className="section-header" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
        <h2
          className="section-title font-bold text-zinc-900 dark:text-zinc-100"
          style={{ fontSize: '2rem' }}
        >
          Get in Touch
        </h2>
      </div>

      <div className="contact-info-wrapper contact-info-item">
        <i className="fas fa-envelope text-purple-600 dark:text-white" />
        <div>
          <span id="email-placeholder" className="email-link">
            Contact Us
          </span>
        </div>
      </div>

      <div className="contact-info-wrapper contact-info-item">
        <i className="fas fa-phone text-purple-600 dark:text-white" />
        <div>
          <form
            className="phone-back-form"
            id="phoneBackForm"
            onSubmit={handlePhoneBackSubmit}
            ref={phoneBackFormRef}
          >
            <input
              type="tel"
              id="phoneInput"
              name="phone"
              autoComplete="tel"
              placeholder="07474 222333"
              required
              aria-label="Phone number for callback"
              ref={phoneInputRef}
              className="contact-input-shared"
            />
            <button type="submit" className="btn-purple whitespace-nowrap">
              Call Back
            </button>
          </form>
        </div>
      </div>

      <div className="contact-form-wrapper">
        <div className="contact-form">
          <div className="contact-form-title-wrapper">
            <i className="fas fa-paper-plane text-purple-600 dark:text-white" />
            <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Send us a message:</h3>
          </div>
          <form
            id="contactForm"
            className="main-contact-grid"
            onSubmit={handleContactSubmit}
            ref={contactFormRef}
          >
            <div className="form-group">
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Your Name"
                required
                autoComplete="name"
                aria-label="Your Name"
                className="contact-input-shared"
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Your Email"
                required
                autoComplete="email"
                aria-label="Your Email"
                className="contact-input-shared"
              />
            </div>
            <div className="form-group">
              <input
                type="tel"
                id="phone"
                name="phone"
                autoComplete="tel"
                placeholder="07474 222333"
                aria-label="Your Phone Number"
                ref={messagePhoneInputRef}
                className="contact-input-shared"
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                id="company"
                name="company"
                placeholder="Your Company"
                autoComplete="organization"
                aria-label="Your Company"
                className="contact-input-shared"
              />
            </div>
            <div className="form-group full-width">
              <textarea
                id="message"
                name="message"
                placeholder="Your Message"
                rows={4}
                required
                aria-label="Message"
                className="contact-input-shared"
              />
            </div>
            <button type="submit" className="btn-purple w-full">
              Send Message
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
